import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { R as GeneralResources } from '@/Resources.resx';
import { R } from '@/features/programs/resources/ProgramsResources.resx';

import { SundVaegtProgramDto } from '@/shared/interfaces/api/sundvaegt-interfaces';
import { fetchSundVaegtProgramQueryAtom } from './fetchSundVaegtProgramQueryAtom';
import { softDeleteWeightLog } from '../api/softDeleteWeightLog';

const queryKey = ['sundvaegt-weightprogram'] as const;

export const deleteWeightLogMutationAtom = atomWithMutation<
    string, // TData (return type)
    number, // TVariables (you pass weightLog Id)
    Error, // TError
    { previous?: SundVaegtProgramDto } // TContext (used for rollback)
>((get) => {
    const queryClient = get(queryClientAtom);

    return {
        mutationFn: async (weightLogId: number) => {
            // read the freshest program right before calling the API
            const program = get(fetchSundVaegtProgramQueryAtom).data;

            if (!program) {
                throw new Error('No SundVægt program loaded');
            }
            try {
                return await softDeleteWeightLog(weightLogId);
            } catch (error) {
                throw new Error(
                    `${R.SundvaegtDeleteWeightLogErrorMessage}: ${
                        error instanceof Error
                            ? error.message
                            : GeneralResources.UnknownError
                    }`,
                );
            }
        },

        // optimistic update
        onMutate: async (weightLogId: number) => {
            await queryClient.cancelQueries({ queryKey });

            const previous =
                queryClient.getQueryData<SundVaegtProgramDto>(queryKey);

            if (previous) {
                queryClient.setQueryData<SundVaegtProgramDto>(queryKey, {
                    ...previous,
                    WeightLogs: previous.WeightLogs.filter(
                        (log) => log.Id !== weightLogId,
                    ),
                });
            }
            return { previous };
        },

        // rollback on error
        onError: (_err, _vars, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData<SundVaegtProgramDto>(
                    queryKey,
                    ctx.previous,
                );
            }
        },

        // ensure server truth after success
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey });
        },
    };
});
